import type { AxiosInstance } from 'axios';
import { logger } from '@parrot-software/observability';

import type { AuthService } from 'types/AuthService';

import type {
  TableServiceApiRequest,
  CreateServiceAreaApiRequest,
  CreateServiceAreaApiResponse,
  ServiceAreaApiResponse,
  BulkCreateTablesApiRequest,
  StoreSettingsApiRequest,
  StoreSettingsApiResponse,
  PayConfigUpdateRequest,
  TaxApiGroup,
  SetDefaultTaxApiRequest,
  StoreAdminApiResponse,
  BrandConfigApiItem,
  HubspotPropertiesApiRequest,
  CompleteWizardApiResponse,
} from './types';

const onbLog = logger.forDomain('ONB');

export type OnboardingHttpClientType = {
  completeWizard: () => Promise<CompleteWizardApiResponse>;
  applyTableService: (request: TableServiceApiRequest) => Promise<void>;
  getServiceAreas: () => Promise<ServiceAreaApiResponse[]>;
  createServiceArea: (request: CreateServiceAreaApiRequest) => Promise<CreateServiceAreaApiResponse>;
  bulkCreateTables: (request: BulkCreateTablesApiRequest) => Promise<void>;
  getStoreSettings: () => Promise<StoreSettingsApiResponse>;
  updateStoreSettings: (request: StoreSettingsApiRequest) => Promise<void>;
  updatePayConfig: (request: PayConfigUpdateRequest) => Promise<void>;
  getTaxes: () => Promise<TaxApiGroup[]>;
  setDefaultTax: (request: SetDefaultTaxApiRequest) => Promise<void>;
  getStoreAdminInfo: () => Promise<StoreAdminApiResponse>;
  getStoreBrands: () => Promise<BrandConfigApiItem[]>;
  updateBrandReceiptHeader: (brandUuid: string, headerText: string) => Promise<void>;
  updateHubspotProperties: (request: HubspotPropertiesApiRequest) => Promise<void>;
};

/**
 * Creates the HTTP client for Onboarding API operations.
 *
 * @param props.httpClient - Axios instance configured for the API
 * @param props.authService - Authentication service to get store UUID
 * @returns OnboardingHttpClientType
 */
export const createOnboardingHttpClient = (props: {
  httpClient: AxiosInstance;
  payHttpClient: AxiosInstance;
  adminHttpClient: AxiosInstance;
  authService: AuthService;
}): OnboardingHttpClientType => {
  const { httpClient, payHttpClient, adminHttpClient, authService } = props;

  const completeWizard = async (): Promise<CompleteWizardApiResponse> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      onbLog.error('onb_http_missing_store_uuid', { 'onb.operation': 'completeWizard' });
      throw new Error('Store UUID is required to complete the wizard');
    }

    onbLog.debug('onb_complete_wizard_request');
    const response = await httpClient.patch<CompleteWizardApiResponse>(
      '/auth/internal/ob-account/stores/complete-wizard/',
      { storeUuid },
    );
    return response.data;
  };

  const applyTableService = async (request: TableServiceApiRequest): Promise<void> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to apply table service configuration');
    }

    await httpClient.put(`/v1/store-table-settings/?store_uuid=${storeUuid}`, request);
  };

  const getServiceAreas = async (): Promise<ServiceAreaApiResponse[]> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to fetch service areas');
    }

    const response = await httpClient.get<{ data: ServiceAreaApiResponse[] }>(
      `/v1/service-areas/?store_uuid=${storeUuid}`,
    );
    return response.data.data;
  };

  const createServiceArea = async (request: CreateServiceAreaApiRequest): Promise<CreateServiceAreaApiResponse> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to create a service area');
    }

    const response = await httpClient.post<{ data: CreateServiceAreaApiResponse }>(
      `/v1/service-areas/?store_uuid=${storeUuid}`,
      request,
    );
    return response.data.data;
  };

  const bulkCreateTables = async (request: BulkCreateTablesApiRequest): Promise<void> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to bulk create tables');
    }

    await httpClient.post(`/v1/area-tables/bulk-create/?store_uuid=${storeUuid}`, request);
  };

  const getStoreSettings = async (): Promise<StoreSettingsApiResponse> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to fetch store settings');
    }

    const response = await httpClient.get<{ data: StoreSettingsApiResponse }>(
      `/v1/stores/settings/?store_uuid=${storeUuid}`,
    );
    return response.data.data;
  };

  const updateStoreSettings = async (request: StoreSettingsApiRequest): Promise<void> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to update store settings');
    }

    await httpClient.put(`/v1/stores/settings/?store_uuid=${storeUuid}`, request);
  };

  const updatePayConfig = async (request: PayConfigUpdateRequest): Promise<void> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to update pay configuration');
    }

    await payHttpClient.put(`/stores/${storeUuid}/configuration`, { ...request, storeUuid });
  };

  const getTaxes = async (): Promise<TaxApiGroup[]> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to fetch taxes');
    }

    const response = await httpClient.get<{ data: TaxApiGroup[] }>(`/v1/taxes/?store_uuid=${storeUuid}`);
    return response.data.data;
  };

  const getStoreAdminInfo = async (): Promise<StoreAdminApiResponse> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to fetch store admin info');
    }

    const adminToken = process.env.REACT_APP_ONBOARDING_ADMIN_TOKEN;
    if (!adminToken) {
      onbLog.debug('onb_admin_token_missing');
      throw new Error('REACT_APP_ONBOARDING_ADMIN_TOKEN env var is not configured');
    }

    const response = await adminHttpClient.get<StoreAdminApiResponse>(`/v1/admin/stores/${storeUuid}`, {
      headers: { 'X-Admin-Token': adminToken },
    });
    return response.data;
  };

  const getStoreBrands = async (): Promise<BrandConfigApiItem[]> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to fetch brands');
    }

    const response = await httpClient.get<{ data: BrandConfigApiItem[] }>(
      `/v1/stores/brands/config/?store_uuid=${storeUuid}`,
    );
    return response.data.data;
  };

  const updateBrandReceiptHeader = async (brandUuid: string, headerText: string): Promise<void> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to update the brand receipt header');
    }

    const formData = new FormData();
    formData.append('headerText', headerText);
    formData.append('footerText', '');

    await httpClient.put(`/v1/stores/brands/${brandUuid}/config/?store_uuid=${storeUuid}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const setDefaultTax = async (request: SetDefaultTaxApiRequest): Promise<void> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to set the default tax');
    }

    await httpClient.put(`/v1/stores/${storeUuid}/config/taxes/`, request);
  };

  const updateHubspotProperties = async (request: HubspotPropertiesApiRequest): Promise<void> => {
    const storeUuid = authService?.store?.uuid;

    if (!storeUuid) {
      throw new Error('Store UUID is required to update HubSpot properties');
    }

    await httpClient.patch(`/auth/internal/ob-account/stores/${storeUuid}/hubspot-properties/`, request);
  };

  return {
    completeWizard,
    applyTableService,
    getServiceAreas,
    createServiceArea,
    bulkCreateTables,
    getStoreSettings,
    updateStoreSettings,
    updatePayConfig,
    getTaxes,
    setDefaultTax,
    getStoreAdminInfo,
    getStoreBrands,
    updateBrandReceiptHeader,
    updateHubspotProperties,
  };
};
